import { useState, useMemo } from 'react'
import { ShieldCheck, Plus, ArrowDownCircle, RotateCcw, Calendar, Info } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Button, Spinner, Select, ConfirmDialog } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { PermissionGate } from '@/components/common/PermissionGate'
import { usePermissions } from '@/hooks/usePermissions'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { useMe } from '@/features/settings/hooks/useMe'
import {
  useCompanies,
  useMyCompanies,
  useCompany,
  useCompanyMembers,
} from '@/features/companies/hooks/useCompanies'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import {
  useSecurityBalanceSummary,
  useSecurityTransactions,
  useApplyMonthlyDeduction,
} from '@/features/security-ledger/hooks/useSecurityLedger'
import { getMonthlyDeductionAmount } from '@/features/security-ledger/schemas/security-ledger.schemas'
import { formatLedgerAmount } from '@/features/security-ledger/lib/format'
import type {
  LedgerTransactionType,
  LedgerTransactionStatus,
} from '@/features/security-ledger/types/security-ledger.types'
import { usePagedListParams, type PagedListBaseParams } from '@/hooks/usePagedListParams'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { SecurityLedgerTable } from './SecurityLedgerTable'
import { DeductionModal } from './DeductionModal'
import { AdjustmentModal } from './AdjustmentModal'
import { RefundRequestModal } from './RefundRequestModal'
import {
  getMemberId,
  getMemberLabel,
  getMemberSubline,
} from '@/features/companies/utils/memberLabel'
import {
  ledgerListsAllCompanies,
  resolveLedgerSubject,
} from '@/features/security-ledger/lib/ledgerSubject'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

const TYPE_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All types' },
  { value: 'deduction', label: 'Deduction' },
  { value: 'manual_adjustment', label: 'Adjustment' },
  { value: 'refund', label: 'Refund' },
]

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending_approval', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
]

function isSameMonth(iso: string, ref: Date): boolean {
  const d = new Date(iso)
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth()
}

/** Info icon that reveals the "how it works" help on hover/focus. */
function HowItWorksInfo(): React.ReactElement {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label="How security deposits work"
        className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <Info className="h-4 w-4" aria-hidden />
        How it works
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-20 mt-2 w-80 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800 opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200"
      >
        Deductions and manual adjustments apply immediately and raise the held balance. A refund is
        created as a request and only reduces the balance once an approver approves it — rejecting a
        request leaves the balance unchanged. Every action is recorded in the audit log.
      </span>
    </span>
  )
}

export function WorkspaceSecurityLedgerPage(): React.ReactElement {
  const { can } = usePermissions()
  const { data: me } = useMe()

  const hasRead = can('security_ledger', 'read')
  const canPostDeduction = can('security_ledger', 'post_deduction')
  const canPostAdjustment = can('security_ledger', 'post_adjustment')
  const canRequestRefund = can('security_ledger', 'request_refund')
  const canApproveRefund = can('security_ledger', 'approve_refund')
  const canRejectRefund = can('security_ledger', 'reject_refund')
  const canDecideRefund = canApproveRefund || canRejectRefund
  const hasWriteAccess =
    canPostDeduction || canPostAdjustment || canRequestRefund || canDecideRefund

  // Writers with company read can act on every company even when they hold no
  // personal membership; everyone else sees only companies they belong to.
  // Without this branch an org admin (no membership) gets an empty list and
  // the page spins forever.
  const listAllCompanies = ledgerListsAllCompanies(can('companies', 'read'), hasWriteAccess)
  const allCompaniesQuery = useCompanies(undefined, { enabled: listAllCompanies })
  const myCompaniesQuery = useMyCompanies({ enabled: !listAllCompanies })
  const companies = useMemo(
    () => (listAllCompanies ? (allCompaniesQuery.data ?? []) : (myCompaniesQuery.data ?? [])),
    [listAllCompanies, allCompaniesQuery.data, myCompaniesQuery.data]
  )
  const companiesLoading = listAllCompanies
    ? allCompaniesQuery.isLoading
    : myCompaniesQuery.isLoading

  // Single-select company. Default to ?companyId= from URL, then first available company.
  const [companyId, setCompanyId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('companyId')
  })
  const effectiveCompanyId = companyId ?? companies[0]?._id ?? null
  const selectedCompanyIdSet = useMemo(
    () => (effectiveCompanyId ? new Set([effectiveCompanyId]) : new Set<string>()),
    [effectiveCompanyId]
  )

  const { data: company } = useCompany(effectiveCompanyId)
  const { data: membersPage, isLoading: membersLoading } = useCompanyMembers(
    hasWriteAccess ? effectiveCompanyId : null,
    { page: 1, limit: 100 }
  )
  const members = useMemo(() => membersPage?.data ?? [], [membersPage])

  const myUserId = me?._id ?? null

  const employeeOptions: SelectOption[] = useMemo(() => {
    if (!hasWriteAccess) return []
    return members.map((m) => ({
      value: getMemberId(m),
      label: getMemberLabel(m),
      description: getMemberSubline(m),
    }))
  }, [members, hasWriteAccess])

  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const memberIds = useMemo(
    () => (membersPage ? employeeOptions.map((o) => o.value) : undefined),
    [membersPage, employeeOptions]
  )
  const effectiveUserId =
    resolveLedgerSubject({ hasWriteAccess, selectedUserId, memberIds, myUserId }) ?? ''
  const companyHasNoMembers = hasWriteAccess && !!membersPage && members.length === 0

  const selectedEmployeeName = useMemo(() => {
    const opt = employeeOptions.find((o) => o.value === effectiveUserId)
    if (opt) return opt.label
    if (effectiveUserId && effectiveUserId === myUserId) {
      const name = [me?.firstName, me?.lastName].filter(Boolean).join(' ').trim()
      return name || me?.email || 'You'
    }
    return ''
  }, [employeeOptions, effectiveUserId, myUserId, me])

  const applyMonthly = useApplyMonthlyDeduction()
  const [monthlyConfirmOpen, setMonthlyConfirmOpen] = useState(false)
  const [deductionOpen, setDeductionOpen] = useState(false)
  const [adjustmentOpen, setAdjustmentOpen] = useState(false)
  const [refundOpen, setRefundOpen] = useState(false)

  const ledgerEnabled = hasRead && !!effectiveCompanyId && !!effectiveUserId

  const {
    data: summary,
    isLoading: balanceLoading,
    isError: balanceError,
    refetch: balanceRefetch,
  } = useSecurityBalanceSummary(effectiveCompanyId, effectiveUserId || null, {
    enabled: ledgerEnabled,
  })
  const { currency: orgCurrency } = useOrgCurrency()
  const currentBalance = summary?.balance ?? 0
  const currency = summary?.currency || orgCurrency

  // Server-side filters (reset to page 1 when changed).
  const [typeFilter, setTypeFilter] = useState<LedgerTransactionType | ''>('')
  const [statusFilter, setStatusFilter] = useState<LedgerTransactionStatus | ''>('')

  const txPaging = usePagedListParams<PagedListBaseParams>({ page: 1, limit: 10 })
  const txParams = useMemo(
    () => ({
      ...txPaging.params,
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [txPaging.params, typeFilter, statusFilter]
  )
  const {
    data: txPage,
    isLoading: txLoading,
    error: txError,
    refetch: txRefetch,
  } = useSecurityTransactions(effectiveCompanyId, effectiveUserId || null, txParams, {
    enabled: ledgerEnabled,
  })
  const transactions = txPage?.data ?? []
  const txTotal = txPage?.total ?? 0

  // Count of refunds awaiting approval, for the approver quick-filter badge.
  const { data: pendingRefundPage } = useSecurityTransactions(
    effectiveCompanyId,
    effectiveUserId || null,
    { page: 1, limit: 1, type: 'refund', status: 'pending_approval' },
    { enabled: ledgerEnabled && canDecideRefund }
  )
  const pendingRefundCount = pendingRefundPage?.total ?? 0

  const monthlyAmount = getMonthlyDeductionAmount(company?.settings)
  // Best-effort guard: detect a monthly deduction already posted this month from
  // the loaded page (newest-first). The backend stays authoritative.
  const monthlyAlreadyApplied = useMemo(() => {
    const now = new Date()
    return transactions.some(
      (t) =>
        t.type === 'deduction' &&
        (t.note ?? '').toLowerCase().includes('monthly security deduction') &&
        isSameMonth(t.createdAt, now)
    )
  }, [transactions])

  const setTypeFilterReset = (v: string): void => {
    setTypeFilter(v as LedgerTransactionType | '')
    txPaging.setPage(1)
  }
  const setStatusFilterReset = (v: string): void => {
    setStatusFilter(v as LedgerTransactionStatus | '')
    txPaging.setPage(1)
  }
  const showOnlyPendingRefunds = (): void => {
    setTypeFilter('refund')
    setStatusFilter('pending_approval')
    txPaging.setPage(1)
  }

  const handleToggleCompany = (id: string | null): void => {
    if (id === null) return // single-select: ignore "All"
    setCompanyId(id)
    setSelectedUserId('')
  }

  if (!hasRead) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Payroll', to: '/payroll' }, { label: 'Security deposit' }]}
        icon={ShieldCheck}
        title="Security deposit"
        className="gap-0"
        hideHeader
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You do not have permission to view security deposit records.
        </p>
      </PageShell>
    )
  }

  return (
    <>
      <PageShell
        breadcrumbItems={[{ label: 'Payroll', to: '/payroll' }, { label: 'Security deposit' }]}
        icon={ShieldCheck}
        title="Security deposit"
        description="Monthly deductions, adjustments, and refunds for employee security deposits. Pick a company to drill in."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={
              companies.length > 0 ? (
                <CompanyChips
                  companies={companies.map((c) => ({
                    _id: c._id,
                    name: c.name,
                    color: (c as unknown as { color?: string }).color,
                  }))}
                  selectedCompanyIds={selectedCompanyIdSet}
                  onToggle={handleToggleCompany}
                  hideAll
                />
              ) : undefined
            }
            actions={
              canDecideRefund && pendingRefundCount > 0 ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={showOnlyPendingRefunds}
                  className="flex items-center gap-1.5"
                >
                  Pending refunds
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                    {pendingRefundCount}
                  </span>
                </Button>
              ) : null
            }
          >
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <div className="w-44">
                <Select
                  options={TYPE_FILTER_OPTIONS}
                  value={typeFilter}
                  onChange={setTypeFilterReset}
                  aria-label="Filter by type"
                />
              </div>
              <div className="w-44">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={setStatusFilterReset}
                  aria-label="Filter by status"
                />
              </div>
            </div>
          </WorkspaceToolbar>

          {companiesLoading ? (
            <SectionLoader variant="table" cols={7} rows={6} label="Loading security ledger" />
          ) : !effectiveCompanyId ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
              No company available. Please create or join a company first.
            </p>
          ) : hasWriteAccess && membersLoading ? (
            <SectionLoader variant="table" cols={7} rows={6} label="Loading employees" />
          ) : companyHasNoMembers ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
              There are no active employees in this company yet.
            </p>
          ) : (
            <>
              {hasWriteAccess && employeeOptions.length > 0 && (
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="sl-employee-select"
                    className="shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Employee
                  </label>
                  <div className="w-72">
                    <Select
                      id="sl-employee-select"
                      options={employeeOptions}
                      value={effectiveUserId}
                      onChange={(v) => setSelectedUserId(v)}
                      searchable
                      placeholder="Select employee…"
                    />
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-800/50">
                <div className="flex items-center gap-4">
                  <ShieldCheck className="h-6 w-6 shrink-0 text-primary" aria-hidden />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Current balance{selectedEmployeeName ? ` — ${selectedEmployeeName}` : ''}
                      </p>
                      <HowItWorksInfo />
                    </div>
                    {balanceLoading ? (
                      <Spinner size="sm" />
                    ) : balanceError ? (
                      <div className="flex items-center gap-3">
                        <p className="text-sm text-red-700 dark:text-red-300">
                          Couldn’t load balance.
                        </p>
                        <Button variant="secondary" size="sm" onClick={() => balanceRefetch()}>
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {formatLedgerAmount(currentBalance, currency)}
                      </p>
                    )}
                  </div>
                </div>
                {!balanceLoading && !balanceError && summary && (
                  <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 border-t border-gray-200 pt-3 text-xs sm:grid-cols-4 dark:border-gray-700">
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Deducted</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        {formatLedgerAmount(summary.totalDeducted, currency)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Adjusted</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        {formatLedgerAmount(summary.totalAdjusted, currency)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Refunded</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        {formatLedgerAmount(summary.totalRefunded, currency)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Pending refunds</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        {formatLedgerAmount(summary.pendingRefundTotal, currency)}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>

              {effectiveUserId && hasWriteAccess && (
                <div className="flex flex-wrap gap-2">
                  <PermissionGate module="security_ledger" action="post_deduction">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => setDeductionOpen(true)}
                      className="flex items-center gap-1.5"
                    >
                      <ArrowDownCircle className="h-4 w-4" />
                      Manual deduction
                    </Button>
                    {monthlyAmount != null && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setMonthlyConfirmOpen(true)}
                        className="flex items-center gap-1.5"
                        disabled={monthlyAlreadyApplied}
                        loading={applyMonthly.isPending}
                        title={
                          monthlyAlreadyApplied
                            ? 'Monthly deduction already applied this month'
                            : undefined
                        }
                      >
                        <Calendar className="h-4 w-4" />
                        {monthlyAlreadyApplied
                          ? 'Monthly applied'
                          : `Apply monthly (${formatLedgerAmount(monthlyAmount, currency)})`}
                      </Button>
                    )}
                  </PermissionGate>
                  <PermissionGate module="security_ledger" action="post_adjustment">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setAdjustmentOpen(true)}
                      className="flex items-center gap-1.5"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Adjustment
                    </Button>
                  </PermissionGate>
                  <PermissionGate module="security_ledger" action="request_refund">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setRefundOpen(true)}
                      className="flex items-center gap-1.5"
                      disabled={currentBalance <= 0}
                      title={currentBalance <= 0 ? 'No balance available to refund' : undefined}
                    >
                      <Plus className="h-4 w-4" />
                      Request refund
                    </Button>
                  </PermissionGate>
                </div>
              )}

              <SecurityLedgerTable
                companyId={effectiveCompanyId}
                ledgerUserId={effectiveUserId}
                transactions={transactions}
                currency={currency}
                isLoading={txLoading}
                error={txError}
                canApproveRefund={canApproveRefund}
                canRejectRefund={canRejectRefund}
                onRetry={() => txRefetch()}
              />
              <TablePaginationBar
                page={txPaging.params.page}
                limit={txPaging.params.limit}
                total={txTotal}
                totalPages={Math.max(1, Math.ceil(txTotal / txPaging.params.limit))}
                onPageChange={txPaging.setPage}
                onLimitChange={txPaging.setLimit}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </>
          )}
        </div>
      </PageShell>

      {effectiveCompanyId && effectiveUserId && (
        <>
          <DeductionModal
            companyId={effectiveCompanyId}
            targetUserId={effectiveUserId}
            currentBalance={currentBalance}
            currency={currency}
            open={deductionOpen}
            onClose={() => setDeductionOpen(false)}
          />
          <AdjustmentModal
            companyId={effectiveCompanyId}
            targetUserId={effectiveUserId}
            currentBalance={currentBalance}
            currency={currency}
            open={adjustmentOpen}
            onClose={() => setAdjustmentOpen(false)}
          />
          <RefundRequestModal
            companyId={effectiveCompanyId}
            targetUserId={effectiveUserId}
            currentBalance={currentBalance}
            currency={currency}
            open={refundOpen}
            onClose={() => setRefundOpen(false)}
          />
        </>
      )}

      <ConfirmDialog
        open={monthlyConfirmOpen}
        title="Apply monthly deduction"
        message={`Apply the configured monthly security deposit deduction${monthlyAmount != null ? ` of ${formatLedgerAmount(monthlyAmount, currency)}` : ''} for this employee?`}
        confirmLabel="Apply"
        onConfirm={() => {
          if (!effectiveCompanyId || !effectiveUserId) return
          applyMonthly.mutate(
            { companyId: effectiveCompanyId, employeeId: effectiveUserId },
            { onSuccess: () => setMonthlyConfirmOpen(false) }
          )
        }}
        onClose={() => setMonthlyConfirmOpen(false)}
        confirmLoading={applyMonthly.isPending}
      />
    </>
  )
}

WorkspaceSecurityLedgerPage.displayName = 'WorkspaceSecurityLedgerPage'
